import { DragDropContext, DropResult, Droppable } from "@hello-pangea/dnd";
import { Autorenew, FileUploadOutlined } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Button,
  CircularProgress,
  Grid,
  IconButton,
  InputLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  ApiError,
  ApiErrorClass,
  PlanTemplate as PlanTemplateModel,
  PlanTemplatePhase,
  PlanTemplateWeek,
} from "@readyfit-common/models";
import { handleApiError } from "@readyfit-common/utils";
import { EditIcon, TrashIcon } from "src/grading/assets";
import ConfirmationModal from "src/grading/components/ConfirmationModal/ConfirmationModal";
import EditPlanTemplateModal from "src/grading/components/EditPlanTemplateModal/EditPlanTemplateModal";
import GradingHeader from "src/grading/components/GradingHeader/GradingHeader";
import AthletesLayout from "src/grading/layout/AthletesLayout/AthletesLayout";
import { MY_LIBRARY_ROUTES } from "src/grading/routes/MyLibraryRoutes";
import { useAppDispatch, useAppSelector } from "src/store/hooks";
import { useAddPlanTemplateBannerMutation } from "src/store/reducers/documents/documentsApiSlice";
import { useDeletePlanTemplateBannerMutation } from "src/store/reducers/documents/documentsApiSlice";
import {
  addBreadcrumb,
  clearBreadcrumbs,
  selectShowDrawer,
  showSnackbar,
} from "src/store/reducers/navigation/navigationSlice";
import {
  selectDeleteWeekModalOpen,
  selectDeleteWorkoutModalOpen,
  selectSelectedWeek,
  selectSelectedWorkout,
  selectWorkoutModalOpen,
  setDeleteWeekModalOpen,
  setDeleteWorkoutModalOpen,
  setSelectedWeek,
  setSelectedWorkout,
  useCreatePlanTemplatePhaseMutation,
  useCreatePlanTemplateWeekMutation,
  useDeletePlanTemplateWeekMutation,
  useDeletePlanTemplateWeekWorkoutMutation,
  useGetPlanTemplateQuery,
  useSortPlanTemplateMutation,
  useUpdatePlanTemplatesLibraryMutation,
} from "src/store/reducers/training/trainingApiSlice";
import theme from "src/theme";
import planTemplateBanner from "../../assets/images/plan-template-banner.png";
import { ListWeeks } from "../Athletes/AthleteDetails/ListWeeks/ListWeeks";
import useStyles from "./PlanTemplate.style";

const _FIELD_MAP = {
  name: "Name",
  scheduledMessage: "Scheduled Message",
  planDescription: "Plan Description",
  planInfo: "Plan Details",
};

const PlanTemplate = () => {
  const showDrawer = useAppSelector(selectShowDrawer);
  const classes = useStyles(showDrawer)();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const [isEditingName, setIsEditingName] = useState<boolean>(false);
  const { id } = useParams<{ id: string }>();

  const { data } = useGetPlanTemplateQuery(id);
  const [planTemplate, setPlanTemplate] = useState<PlanTemplateModel | null>(
    null,
  );
  const [updatePlanTemplate] = useUpdatePlanTemplatesLibraryMutation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [sortPlanTemplate] = useSortPlanTemplateMutation();
  const [
    createPlanTemplateWeek,
    { isLoading: isLoadingCreatePlanTemplateWeek },
  ] = useCreatePlanTemplateWeekMutation();
  const [
    createPlanTemplatePhase,
    { isLoading: isLoadingCreatePlanTemplatePhase },
  ] = useCreatePlanTemplatePhaseMutation();
  const [
    deletePlanTemplateWeek,
    { isLoading: isLoadingDeletePlanTemplateWeek },
  ] = useDeletePlanTemplateWeekMutation();
  const selectedWeek = useAppSelector(selectSelectedWeek);
  const deleteWeekModalOpen = useAppSelector(selectDeleteWeekModalOpen);
  const [addPlanTemplateBanner] = useAddPlanTemplateBannerMutation();
  const [
    deletePlanTemplateBanner,
    { isLoading: isLoadingDeletePlanTemplateBanner },
  ] = useDeletePlanTemplateBannerMutation();
  const [bannerLoaded, setBannerLoaded] = useState(false);
  const [deleteBannerModalOpen, setDeleteBannerModalOpen] = useState(false);
  const workoutModalOpen = useAppSelector(selectWorkoutModalOpen);
  const deleteWorkoutModalOpen = useAppSelector(selectDeleteWorkoutModalOpen);
  const [
    deletePlanTemplateWorkout,
    { isLoading: isLoadingDeletePlanTemplateWorkout },
  ] = useDeletePlanTemplateWeekWorkoutMutation();
  const selectedWorkout = useAppSelector(selectSelectedWorkout);

  useEffect(() => {
    if (data?.data?.plan) {
      setPlanTemplate(data?.data?.plan);
    }
  }, [data]);

  useEffect(() => {
    dispatch(
      addBreadcrumb({
        label: "Plan Templates",
        path: MY_LIBRARY_ROUTES.PLAN_TEMPLATES,
      }),
    );

    return () => {
      dispatch(clearBreadcrumbs());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      planTemplate?.bannerImageUrls &&
      planTemplate?.bannerImageUrls?.length > 0
    ) {
      const img = new Image();
      img.onload = () => setBannerLoaded(true);
      img.src = planTemplate.bannerImageUrls[0];
    } else {
      const img = new Image();
      img.onload = () => setBannerLoaded(true);
      img.src = planTemplateBanner;
    }
  }, [planTemplate?.bannerImageUrls]);

  const handleUpdatePlanTemplate = (
    field: keyof PlanTemplateModel,
    value: string | string[],
  ) => {
    try {
      updatePlanTemplate({
        id,
        [field]: value,
      }).unwrap();

      dispatch(
        showSnackbar({
          open: true,
          message: `Plan Template updated successfully`,
          type: "success",
        }),
      );
    } catch (error: unknown) {
      const typedError = error as ApiError;
      handleApiError(dispatch, typedError);
    }
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    try {
      const file = event.target.files?.[0];
      if (file) {
        // Validate file type
        const allowedTypes = [
          "image/jpeg",
          "image/jpg",
          "image/png",
          "image/webp",
        ];
        if (!allowedTypes.includes(file.type)) {
          dispatch(
            showSnackbar({
              message: "Please select a valid image file (JPEG, PNG, or WebP)",
              type: "error",
            }),
          );
          return;
        }

        // Validate file size (e.g., max 5MB)
        const maxSize = 5 * 1024 * 1024; // 5MB
        if (file.size > maxSize) {
          dispatch(
            showSnackbar({
              message: "File size must be less than 5MB",
              type: "error",
            }),
          );
          return;
        }

        if (
          planTemplate?.bannerImageUrls &&
          planTemplate?.bannerImageUrls?.length > 0
        ) {
          await deletePlanTemplateBanner({
            planTemplateId: id,
            body: { urls: planTemplate.bannerImageUrls },
          }).unwrap();
        }

        const formData = new FormData();
        formData.append("images", file);
        await addPlanTemplateBanner({
          planTemplateId: id,
          body: formData,
        }).unwrap();

        dispatch(
          showSnackbar({
            message: "Banner added successfully",
            type: "success",
          }),
        );
      }
    } catch (error: unknown) {
      const typedError = error as ApiError;
      handleApiError(dispatch, typedError);
    }
  };

  const handleDeletePlanTemplateBanner = async () => {
    try {
      if (!planTemplate?.id) {
        throw new ApiErrorClass({
          message: "Plan Template ID is required",
        });
      }

      await deletePlanTemplateBanner({
        planTemplateId: planTemplate.id,
        body: { urls: planTemplate.bannerImageUrls },
      }).unwrap();

      dispatch(
        showSnackbar({
          open: true,
          message: "Banner deleted successfully",
        }),
      );
      setDeleteBannerModalOpen(false);
    } catch (error: unknown) {
      const typedError = error as ApiError;
      handleApiError(dispatch, typedError);
    }
  };

  const findPhase = (id: number) =>
    planTemplate?.phaseTemplates?.find((phase) => phase.numberOfPhase === id);
  const findWeek = (phase: PlanTemplatePhase, id: number) =>
    phase?.weekTemplates?.find((i, index) => index === id);

  const onDragEnd = async (result: DropResult) => {
    const { source, destination } = result;
    if (
      !destination ||
      !source ||
      (destination.index === source.index &&
        destination.droppableId === source.droppableId)
    ) {
      return null;
    }

    const sourcePhase = findPhase(+source.droppableId);
    const destinationPhase = findPhase(+destination.droppableId);

    if (!sourcePhase || !destinationPhase) {
      return;
    }

    let newPhases: PlanTemplatePhase[] = [];
    let newWeeks: PlanTemplateWeek[] = [];

    if (+source.droppableId === +destination.droppableId) {
      const phase = findPhase(+source.droppableId);
      if (!phase) {
        return;
      }

      const fromPhaseWeek = findWeek(phase, source.index);
      const toPhaseWeek = findWeek(phase, destination.index);

      if (!fromPhaseWeek || !toPhaseWeek) {
        return;
      }

      const newFromPhaseWeek = {
        ...fromPhaseWeek,
        workoutTemplates: fromPhaseWeek.workoutTemplates.map((i, ind) => ({
          ...i,
          // date: toPhaseWeek?.workoutTemplates[ind]?.date,
        })),
      };
      const newToPhaseWeek = {
        ...toPhaseWeek,
        workoutTemplates: toPhaseWeek?.workoutTemplates.map((i, ind) => ({
          ...i,
          // date: fromPhaseWeek?.workoutTemplates[ind]?.date,
        })),
      };

      newWeeks = phase.weekTemplates.map((week) =>
        week.id === newFromPhaseWeek.id
          ? newToPhaseWeek
          : week.id === newToPhaseWeek.id
          ? newFromPhaseWeek
          : week,
      );

      const weeks = newWeeks.map((week) => ({
        ...week,
        id: week.id,
        numberOfWeek: week.numberOfWeek,
      }));

      const newPhase = {
        ...phase,
        numberOfPhase: phase.numberOfPhase,
        weekTemplates: weeks,
      };

      if (!planTemplate) {
        return;
      }

      newPhases = [newPhase];
    }

    if (!planTemplate) {
      return;
    }

    setPlanTemplate({
      ...planTemplate,
      phaseTemplates: newPhases,
    });

    const newPhaseOrder = newPhases.map((phase) => {
      return {
        id: phase.id,
        weeks: phase.weekTemplates.map((week) => ({
          id: week.id,
        })),
      };
    });

    if (!planTemplate?.id) {
      console.error("Plan ID is missing");
      return;
    }

    await sortPlanTemplate({ id: planTemplate?.id, phases: newPhaseOrder });
  };

  const handleCreatePlanTemplateWeek = async () => {
    try {
      if (!planTemplate?.id) {
        throw new ApiErrorClass({
          message: "Plan Template ID is required",
        });
      }

      // If plan template has no phases, create a default phase
      let phaseId = planTemplate?.phaseTemplates?.[0]?.id;
      if (!planTemplate?.phaseTemplates?.length) {
        const response = await createPlanTemplatePhase(
          planTemplate.id,
        ).unwrap();
        console.log("response", response);
        phaseId = response.data.phase.id;
      }

      await createPlanTemplateWeek({
        planTemplateId: planTemplate.id,
        phaseId,
      }).unwrap();

      dispatch(
        showSnackbar({
          open: true,
          message: "Week created successfully",
        }),
      );
    } catch (error: unknown) {
      const typedError = error as ApiError;
      handleApiError(dispatch, typedError);
    }
  };

  const handleDeletePlanTemplateWeek = async () => {
    try {
      if (!planTemplate?.id) {
        throw new ApiErrorClass({
          message: "Plan Template ID is required",
        });
      }

      if (!selectedWeek?.phaseId || !selectedWeek?.id) {
        throw new ApiErrorClass({
          message: "Week ID is required",
        });
      }

      await deletePlanTemplateWeek({
        planTemplateId: planTemplate.id,
        phaseId: selectedWeek?.phaseId,
        weekId: selectedWeek?.id,
      }).unwrap();

      dispatch(
        showSnackbar({
          open: true,
          message: "Week deleted successfully",
        }),
      );

      handleCloseDeleteWeekModal();
    } catch (error: unknown) {
      const typedError = error as ApiError;
      handleApiError(dispatch, typedError);
    }
  };

  const handleCloseDeleteWeekModal = () => {
    dispatch(setSelectedWeek(null));
    dispatch(setDeleteWeekModalOpen(false));
  };

  const handleCloseDeleteBannerModal = () => {
    setDeleteBannerModalOpen(false);
  };

  const handleCloseDeleteWorkoutModal = () => {
    dispatch(setSelectedWorkout(null));
    dispatch(setDeleteWorkoutModalOpen(false));
    dispatch(setSelectedWeek(null));
  };

  const handleSubmitDeleteWorkout = async () => {
    try {
      if (!selectedWorkout?.id) {
        throw new Error("No workout id");
      }

      const response = await deletePlanTemplateWorkout({
        planTemplateId: id || "",
        weekId: selectedWeek?.id || "",
        workoutId: selectedWorkout.id,
        phaseId: planTemplate?.phaseTemplates?.[0]?.id || "",
      }).unwrap();

      if (!response.success) throw new Error(response.message);

      dispatch(
        showSnackbar({
          open: true,
          message: "Workout deleted successfully",
        }),
      );
    } catch (error) {
      console.log("error", error);
      dispatch(
        showSnackbar({
          open: true,
          message: "Error deleting workout",
          type: "error",
        }),
      );
    }

    handleCloseDeleteWorkoutModal();
  };

  return (
    <AthletesLayout>
      <Grid container className={classes.container}>
        {/* BREADCRUMBS AND HEADER */}
        <Grid container item alignItems={"center"}>
          <Grid item>
            <Grid container alignItems={"center"} rowGap={1}>
              <Grid item container>
                <GradingHeader
                  onClick={() => history.push(MY_LIBRARY_ROUTES.PLAN_TEMPLATES)}
                  withBreadcrumbs
                />
              </Grid>
              <Grid item>
                <Typography variant="h2">My Library | Plan Template</Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {/* TITLE */}
        <Grid container alignItems={"center"}>
          {isEditingName ? (
            <TextField
              variant="standard"
              InputProps={{
                disableUnderline: false,
                style: { fontSize: "24px", fontWeight: "bold" },
              }}
              autoFocus
              value={planTemplate?.name}
              onChange={(e) => {
                if (!planTemplate) return;
                setPlanTemplate({
                  ...planTemplate,
                  name: e.target.value || "",
                });
              }}
              onBlur={() => {
                setIsEditingName(false);
                handleUpdatePlanTemplate("name", planTemplate?.name || "");
              }}
              onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                if (e.key === "Enter") {
                  setIsEditingName(false);
                  handleUpdatePlanTemplate("name", planTemplate?.name || "");
                }
              }}
            />
          ) : (
            <Grid container alignItems="center">
              <Typography
                variant="h4"
                onClick={() => {
                  setIsEditingName(true);
                }}
                sx={{ cursor: "pointer" }}
              >
                {planTemplate?.name}
              </Typography>

              <IconButton onClick={() => setIsEditingName(true)}>
                <EditIcon />
              </IconButton>
            </Grid>
          )}
        </Grid>

        {/* PLAN BANNER */}
        <Grid container item>
          <Grid container item flexDirection={"column"}>
            <Typography variant="button" color={"#A0A0A0"}>
              Plan Banner
            </Typography>
            <Typography variant="subtitle1" color={"#000"}>
              Image size: minimum 400x300px | 72dpi
            </Typography>
          </Grid>
          {/* // Add loading state */}
          {!bannerLoaded &&
            (planTemplate?.bannerImageUrls?.length || planTemplateBanner) && (
              <Grid
                container
                item
                style={{
                  minHeight: "344px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CircularProgress />
              </Grid>
            )}
          {(bannerLoaded ||
            (!planTemplate?.bannerImageUrls?.length &&
              !planTemplateBanner)) && (
            <Grid
              container
              item
              sx={
                planTemplate?.bannerImageUrls?.[0]
                  ? {
                      backgroundImage: `url(${
                        planTemplate?.bannerImageUrls?.[0] || planTemplateBanner
                      })`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      backgroundRepeat: "no-repeat",
                      width: "100%",
                      minHeight: "344px",
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "flex-end",
                      position: "relative",
                      borderRadius: "8px",
                      marginTop: "16px",
                    }
                  : {
                      backgroundImage: `url(${planTemplateBanner})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      backgroundRepeat: "no-repeat",
                      width: "100%",
                      minHeight: "344px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      position: "relative",
                      borderRadius: "8px",
                      marginTop: "16px",
                    }
              }
            >
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept="image/*"
                style={{ display: "none" }}
              />
              {!planTemplate?.bannerImageUrls?.length && (
                <Button
                  variant="contained"
                  onClick={() => fileInputRef.current?.click()}
                  className={classes.button}
                >
                  Upload Image
                  <FileUploadOutlined />
                </Button>
              )}

              {planTemplate?.bannerImageUrls &&
                planTemplate?.bannerImageUrls?.length > 0 && (
                  <Grid item className={classes.bannerButtonsContainer}>
                    <Tooltip title="Update Banner">
                      <IconButton
                        className={classes.updateBannerButton}
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <Autorenew sx={{ color: "white" }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Banner">
                      <IconButton className={classes.deleteBannerButton}>
                        <TrashIcon
                          stroke="white"
                          onClick={() => setDeleteBannerModalOpen(true)}
                        />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                )}
            </Grid>
          )}
        </Grid>

        <Grid container item>
          <Grid container item flexDirection={"column"}>
            <Typography variant="button" color={"#A0A0A0"}>
              Template Information
            </Typography>
          </Grid>
          <Grid container item spacing={2} marginTop={2}>
            <Grid item xs={4}>
              <InputLabel htmlFor="name" className={classes.inputLabel}>
                <Typography variant="subtitle1">Welcome Text</Typography>
              </InputLabel>
              <TextField
                multiline
                rows={4}
                fullWidth
                variant="outlined"
                value={planTemplate?.scheduledMessage}
                placeholder="Welcome Text"
                InputProps={{
                  className: classes.input,
                }}
                onChange={(e) => {
                  if (!planTemplate) return;
                  setPlanTemplate({
                    ...planTemplate,
                    scheduledMessage: e.target.value || "",
                  });
                }}
                onBlur={() => {
                  handleUpdatePlanTemplate(
                    "scheduledMessage",
                    planTemplate?.scheduledMessage || "",
                  );
                }}
                onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                  if (e.key === "Enter") {
                    handleUpdatePlanTemplate(
                      "scheduledMessage",
                      planTemplate?.scheduledMessage || "",
                    );
                  }
                }}
              />
            </Grid>

            <Grid item xs={4}>
              <InputLabel htmlFor="name" className={classes.inputLabel}>
                <Typography variant="subtitle1">Plan Description</Typography>
              </InputLabel>
              <TextField
                multiline
                rows={4}
                fullWidth
                variant="outlined"
                value={planTemplate?.planDescription}
                placeholder="Plan Description"
                InputProps={{
                  className: classes.input,
                }}
                onChange={(e) => {
                  if (!planTemplate) return;
                  setPlanTemplate({
                    ...planTemplate,
                    planDescription: e.target.value || "",
                  });
                }}
                onBlur={() => {
                  handleUpdatePlanTemplate(
                    "planDescription",
                    planTemplate?.planDescription || "",
                  );
                }}
                onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                  if (e.key === "Enter") {
                    handleUpdatePlanTemplate(
                      "planDescription",
                      planTemplate?.planDescription || "",
                    );
                  }
                }}
              />
            </Grid>

            <Grid item xs={4}>
              <InputLabel htmlFor="name" className={classes.inputLabel}>
                <Typography variant="subtitle1">Plan Details</Typography>
              </InputLabel>
              <TextField
                multiline
                rows={4}
                fullWidth
                variant="outlined"
                value={planTemplate?.planInfo?.join("\n")}
                placeholder="Plan Details"
                InputProps={{
                  className: classes.input,
                }}
                onChange={(e) => {
                  if (!planTemplate) return;
                  setPlanTemplate({
                    ...planTemplate,
                    planInfo: e.target.value.split("\n"),
                  });
                }}
                onBlur={() => {
                  handleUpdatePlanTemplate(
                    "planInfo",
                    planTemplate?.planInfo || [],
                  );
                }}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid container>
          <DragDropContext onDragEnd={onDragEnd}>
            {planTemplate?.phaseTemplates.map((phase) => {
              return WeekList(phase);
            })}
          </DragDropContext>
        </Grid>

        <Grid container>
          <LoadingButton
            variant="contained"
            onClick={handleCreatePlanTemplateWeek}
            disabled={
              isLoadingCreatePlanTemplatePhase ||
              isLoadingCreatePlanTemplateWeek
            }
            loading={
              isLoadingCreatePlanTemplateWeek ||
              isLoadingCreatePlanTemplatePhase
            }
            className={classes.button}
            sx={{ width: theme.spacing(32) }}
          >
            Add Week
          </LoadingButton>
        </Grid>
      </Grid>
      {planTemplate && (
        <>
          {workoutModalOpen && (
            <EditPlanTemplateModal trainingPlan={planTemplate} />
          )}

          {deleteWorkoutModalOpen && (
            <ConfirmationModal
              onClose={handleCloseDeleteWorkoutModal}
              open={deleteWorkoutModalOpen}
              isLoading={isLoadingDeletePlanTemplateWorkout}
              onConfirm={handleSubmitDeleteWorkout}
              title="Delete Workout"
              message="This action cannot be undone. Are you sure you want to delete the workout?"
              confirmationButtonTitle={"Delete Workout"}
            />
          )}

          {deleteWeekModalOpen && (
            <ConfirmationModal
              onClose={handleCloseDeleteWeekModal}
              open={deleteWeekModalOpen}
              isLoading={isLoadingDeletePlanTemplateWeek}
              onConfirm={handleDeletePlanTemplateWeek}
              title="Delete Week"
              message="This action cannot be undone. Are you sure you want to delete the week?"
              confirmationButtonTitle="Delete Week"
            />
          )}

          {deleteBannerModalOpen && (
            <ConfirmationModal
              onClose={handleCloseDeleteBannerModal}
              open={deleteBannerModalOpen}
              isLoading={isLoadingDeletePlanTemplateBanner}
              onConfirm={handleDeletePlanTemplateBanner}
              title="Remove Background"
              message="This action cannot be undone. Are you sure you want to remove the background?"
              confirmationButtonTitle="Remove Background"
            />
          )}
        </>
      )}
    </AthletesLayout>
  );
};

export const WeekList = (phase: PlanTemplatePhase) => {
  // const [placeholderProps, setPlaceholderProps] = useState({
  //   clientX: 0,
  //   clientY: 0,
  //   clientHeight: 0,
  //   clientWidth: 0,
  //   destination: 0,
  // });
  return (
    <Droppable
      droppableId={phase.numberOfPhase.toString()}
      key={phase.numberOfPhase}
    >
      {(provided, snapshot) => {
        return (
          <div
            {...provided.droppableProps}
            ref={provided.innerRef}
            style={{ position: "relative", width: "100%" }}
          >
            {phase.weekTemplates
              // THIS MAP is to parse type PlanTemplateWeek to PlanWeek
              .map(
                (week) =>
                  ({
                    id: week.id,
                    description: week.description,
                    phaseId: week.phaseTemplateId,
                    numberOfWeek: week.numberOfWeek,
                    workouts: week.workoutTemplates,
                    end: "",
                    start: "",
                  }) as any,
              )
              .map((week, index) => {
                return (
                  <ListWeeks
                    key={week.numberOfWeek}
                    index={index}
                    week={week}
                    numberOfWeeks={phase.weekTemplates.length}
                    currentWeeks={0}
                    workouts={week?.workouts}
                    isEditable
                    isTemplate
                  />
                );
              })}
            {provided.placeholder}
          </div>
        );
      }}
    </Droppable>
  );
};

export default PlanTemplate;
